// MIDAS V3 — the quiet lines under a Variant E status table
// (MIDAS-ANSWER-FORMAT-1 r5b / r5c). Split out of statusTableGrammar.ts.
//
// Fixed forms confirmed by the bot for revision c-6 (MACHINEID_TODO.md
// 2026-10-04, "round 5 / r5b answers", r5b answer 4). Both are composed by the
// bot's loop, never model text:
//
// 1. The "N cannot be verified" sentence: the paragraph right after the
//    table, sent only when N > 0, with numeral agreement:
//      sk  N podmienka sa teraz na stroji nedá overiť.        (N = 1)
//          N podmienky sa teraz na stroji nedajú overiť.      (2–4)
//          N podmienok sa teraz na stroji nedá overiť.        (5+)
//      en  N condition cannot be verified on the machine right now.
//          N conditions cannot be verified on the machine right now.
//      cs  N podmínka se teď na stroji nedá ověřit.
//          N podmínky se teď na stroji nedají ověřit.
//          N podmínek se teď na stroji nedá ověřit.
//    USER-LANG-IT-PL-1 (bot table, MACHINEID_TODO.md 2026-10-31, `25815ce`):
//      it  N condizione non può essere verificata sulla macchina in questo momento.   (1)
//          N condizioni non possono essere verificate sulla macchina in questo momento.
//      pl  N warunek nie może być teraz zweryfikowany na maszynie.        (1)
//          N warunki nie mogą być teraz zweryfikowane na maszynie.        (2–4)
//          N warunków nie może być teraz zweryfikowanych na maszynie.     (5+, also 12–14)
//      de  N Bedingung kann an der Maschine gerade nicht verifiziert werden.   (1)
//          N Bedingungen können an der Maschine gerade nicht verifiziert werden.
//    This is a PHRASE test, not a whole-sentence match, so the earlier
//    prompt-variant wordings ("nelze ověřit", "could not be verified") still
//    match.
// 2. An optional second fixed paragraph, quiet only directly after (1):
//      sk  Stačia červené riadky — výstup <address> je blokovaný bez ohľadu na neoverené.
//      en  The red rows suffice — output <address> is blocked regardless of what is unverified.
//      cs  Stačí červené řádky — výstup <address> je blokován bez ohledu na neověřené.
//      it  Bastano le righe rosse — l'uscita <address> è bloccata indipendentemente da ciò che non è verificato.
//      pl  Wystarczą czerwone wiersze — wyjście <address> jest zablokowane niezależnie od tego, czego nie zweryfikowano.
//      de  Die roten Zeilen genügen — Ausgang <address> ist blockiert, unabhängig davon, was nicht verifiziert ist.
//    This one is a whole-sentence match; only the address varies.
//
// Pure — no React, no DOM.

const UNVERIFIABLE_RE = new RegExp(
  [
    // sk: nedá (1 and 5+) / nedajú (2–4)
    "nedaj[uú]\\s+overi[tť]",
    "ned[aá]\\s+overi[tť]",
    "nepodarilo\\s+(?:sa\\s+)?overi[tť]",
    "nebolo\\s+mo[zž]n[eé]\\s+overi[tť]",
    "nemo[zž]no\\s+overi[tť]",
    // cs: nedá (1 and 5+) / nedají (2–4)
    "nelze\\s+ov[eě][rř]it",
    "nedaj[ií]\\s+ov[eě][rř]it",
    "ned[aá]\\s+ov[eě][rř]it",
    "nepoda[rř]ilo\\s+(?:se\\s+)?ov[eě][rř]it",
    // en
    "(?:cannot|can't|can\\s+not|could\\s+not|couldn't)\\s+(?:\\w+\\s+)?be\\s+verified",
    "unverifiable",
    // it: può (1) / possono (2+)
    "non\\s+(?:pu[oò]|possono)\\s+essere\\s+verificat[ae]",
    // pl: może … zweryfikowany (1) / mogą … zweryfikowane (2–4) /
    // może … zweryfikowanych (5+, 12–14)
    "nie\\s+mo(?:[zż]e|g[aą])\\s+by[cć]\\s+(?:teraz\\s+)?zweryfikowan(?:y|e|ych)",
    // de: kann (1) / können (2+)
    "nicht\\s+verifiziert\\s+werden",
  ].join("|"),
  "i"
);

// "<prefix> — <output address> <suffix>". The address is free text (it may
// even hold a chip sentinel); any dash and spacing around it is tolerated.
const RED_ROWS_SUFFICE_RE = new RegExp(
  "^(?:" +
    [
      "Stačia červené riadky\\s*[—–-]\\s*výstup\\s+.+?\\s+je blokovaný bez ohľadu na neoverené",
      "The red rows suffice\\s*[—–-]\\s*output\\s+.+?\\s+is blocked regardless of what is unverified",
      "Stačí červené řádky\\s*[—–-]\\s*výstup\\s+.+?\\s+je blokován bez ohledu na neověřené",
      "Bastano le righe rosse\\s*[—–-]\\s*l['’]uscita\\s+.+?\\s+è bloccata indipendentemente da ciò che non è verificato",
      "Wystarczą czerwone wiersze\\s*[—–-]\\s*wyjście\\s+.+?\\s+jest zablokowane niezależnie od tego, czego nie zweryfikowano",
      "Die roten Zeilen genügen\\s*[—–-]\\s*Ausgang\\s+.+?\\s+ist blockiert, unabhängig davon, was nicht verifiziert ist",
    ].join("|") +
    ")\\s*\\.?$",
  "i"
);

/** Longer than this is prose, not one of the quiet sentences. */
export const QUIET_LINE_MAX_CHARS = 280;

// NFC first: a decomposed "ť" (t + combining caron) must match like the
// composed one. Whitespace runs (incl. a soft line break) collapse to one space.
const normalise = (text: string): string =>
  (text || "").normalize("NFC").replace(/\s+/g, " ").trim();

/** The "N … cannot be verified" sentence (sk / en / cs / it / pl / de). */
export const isUnverifiableSentence = (text: string): boolean => {
  const t = normalise(text);
  return t.length > 0 && t.length <= QUIET_LINE_MAX_CHARS && UNVERIFIABLE_RE.test(t);
};

/** The optional "red rows suffice — output … is blocked" paragraph. */
export const isRedRowsSufficeSentence = (text: string): boolean => {
  const t = normalise(text);
  return t.length > 0 && t.length <= QUIET_LINE_MAX_CHARS && RED_ROWS_SUFFICE_RE.test(t);
};
